import React from 'react'
import {
  Box, Input, RadioGroup, Radio, Checkbox,
} from '@maui/elements'
import { TIME_SLOTS, TIME_ZONES, REPORT_LEVEL, WEEKDAYS } from '@/utilities'
import { LayoutColumn, LayoutWrapper, Label } from '../common'
import { Divider } from '../../../common/Divider'
import { DropDownSelect } from '../../fields/DropDownSelect'
import { FormNestedCheckbox } from './FormNestedCheckbox'
import { useController, Controller } from 'react-hook-form'
import { useDraft } from '@maui/hooks'

export const ControlledCheckbox = ({ name, control, label, required = false, ...props }) => {
  const { field } = useController({
    control,
    name,
    rules: { required }
  })

  const [value, setValue] = useDraft(field.value)

  return (
    <Checkbox
      {...props}
      onChange={(e) => {
        const valueCopy = [...value]

        if (e.target.checked) {
          valueCopy.push(e.target.value)
        } else {
          const index = valueCopy.indexOf(e.target.value)
          if (index > -1) {
            valueCopy.splice(index, 1)
          }
        }

        field.onChange(valueCopy)
        setValue(valueCopy)
      }}
      value={props.value}
      checked={value.includes(props.value)}
    >
      {label}
    </Checkbox>
  )
}

const Frequency = ({ control, deliveryFreq }) => {
  const [frequencyValue, setFrequencyValue] = React.useState(deliveryFreq || 'DAILY')

  return (
    <LayoutColumn>
    <LayoutColumn gap='spaceGapSmall'>
      <Label htmlFor='deliveryFreq'>
        Frequency
      </Label>
      <Controller
        control={control}
        name='deliveryFreq'
        render={({
          field: { onChange, value, ref },
        }) => (
          <RadioGroup
            onChange={(e) => {
              onChange(e)
              setFrequencyValue(e.target.value)
            }}
            value={value}
            ref={ref}
            display="flex"
            gap="spaceGapXLarge"
          >
            <Radio value="DAILY">
              Daily
            </Radio>
            <Radio value="HOURLY">
              Hourly
            </Radio>
          </RadioGroup>
        )}
      />
    </LayoutColumn>
    {
      frequencyValue === 'HOURLY'
        ? (
        <Box textStyle='bodySmall' whiteSpace='nowrap'>
          You will receive a digest delivery email every hour
        </Box>
          )
        : (
        <>
          <LayoutColumn gap='spaceGapSmall'>
            <Label htmlFor='deliveryDays'>
              Daily Delivery Preferences
            </Label>
            <Box display="flex" flexDirection="row" gap="spaceGapXLarge" alignItems="center">
              {
                WEEKDAYS.map(({ label, value }) => (
                  <ControlledCheckbox
                    key={value}
                    name='deliveryDays'
                    value={value}
                    label={label}
                    control={control}
                  />
                ))
              }
            </Box>
          </LayoutColumn>
          <LayoutWrapper flexWrap='initial'>
            <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge'>
              <Label htmlFor='deliveryTime'>
                Delivery Time
              </Label>
              <Controller
                control={control}
                name='deliveryTime'
                render={({
                  field: { onChange, value, ref },
                }) => (
                  <DropDownSelect
                    onChange={onChange}
                    options={TIME_SLOTS}
                    value={value}
                    ref={ref}
                  />
                )}
              />
              <ControlledCheckbox
                name='enableDayLightSavings'
                control={control}
                label='Ignore Daylight Savings'
                value='dst'
              />
            </LayoutColumn>
            <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge' flex='1'>
              <Label htmlFor='timeZone'>
                Timezone
              </Label>
              <Controller
                control={control}
                name='timeZone'
                render={({
                  field: { onChange, value, ref },
                }) => (
                  <DropDownSelect
                    onChange={onChange}
                    options={TIME_ZONES}
                    value={value}
                    ref={ref}
                  />
                )}
              />
            </LayoutColumn>
          </LayoutWrapper>
        </>
          )
    }
  </LayoutColumn>
  )
}

export const ReportFormContent = ({ register, control, deliveryFreq, errors, hasEntitlements }) => {
  const vulnRiskRatingItems = [
    { label: 'Low', value: 'LOW', access: hasEntitlements(['INTEL_RBI_VULN']) },
    { label: 'Medium', value: 'MEDIUM', access: hasEntitlements(['INTEL_RBI_VULN']) },
    { label: 'High', value: 'HIGH', access: hasEntitlements(['INTEL_RBI_VULN']) },
    { label: 'Critical', value: 'CRITICAL', access: hasEntitlements(['INTEL_RBI_VULN']) }
  ]

  return (
    <>
      <LayoutWrapper mb='spaceGapXLarge'>
        <LayoutColumn maxWidth="256px" width='100%'>
          <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge'>
            <Label htmlFor='name'>
              Name
            </Label>
            <Input size="medium" name='name' id='name' {...register('name', { required: true })} variant={errors?.name || errors?.root?.serverError?.message.includes('name') ? 'error' : 'primary'}/>
          </LayoutColumn>
          <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge'>
            <Label htmlFor='emailAddress'>
              Mailing to
            </Label>
            <Input size="medium" name='emailAddress' id='emailAddress' {...register('emailAddress')} disabled/>
          </LayoutColumn>
          <LayoutColumn gap='spaceGapSmall' mb='spaceGapXLarge'>
            <Label htmlFor='levelOfDetail'>
              Level of Detail
            </Label>
            <Controller
              control={control}
              name='levelOfDetail'
              render={({
                field: { onChange, value, ref },
              }) => (
                <DropDownSelect
                  onChange={onChange}
                  options={REPORT_LEVEL}
                  value={value}
                  ref={ref}
                />
              )}
            />
          </LayoutColumn>
        </LayoutColumn>
        <Frequency control={control} deliveryFreq={deliveryFreq} />
      </LayoutWrapper>
      <LayoutWrapper flex='1'>
        <LayoutColumn width='100%' gap='spaceGapMedium'>
          <Label>
            Options
          </Label>
          <ControlledCheckbox
            name='format'
            control={control}
            label='Format as Text Only (Default is HTML)'
            value='format'
          />
          <Box display="flex" flexDirection="row" gap="spaceGapLarge" alignItems="center">
            <Label>
              Intelligence Packages
            </Label>
            <Divider mt="0" flex="1"/>
          </Box>
          <ControlledCheckbox
            name='intelPackages'
            control={control}
            label='Fusion'
            value='Fusion'
            gap="spaceGapSmall"
            ml="20px"
            disabled={!hasEntitlements(['INTEL_RBI_FUS'])}
            required={true}
          />
          <ControlledCheckbox
            name='intelPackages'
            control={control}
            label='Operational'
            value='Operational'
            gap="spaceGapSmall"
            ml="20px"
            disabled={!hasEntitlements(['INTEL_RBI_OPS'])}
            required={true}
            />
          <FormNestedCheckbox control={control} options={vulnRiskRatingItems} />
          <ControlledCheckbox
            name='intelPackages'
            control={control}
            label='News Analysis'
            value='News Analysis'
            gap="spaceGapSmall"
            ml="20px"
            required={true}
          />
        </LayoutColumn>
      </LayoutWrapper>
    </>
  )
}
